import { IPaymentOfferModel } from "@nf/common";
import { ROUTES } from "@nf/constants";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";

import { useIsAuthenticated, useOffers } from "hooks";
import { Divider, OfferCard } from "components";
import { VoucherLink } from "../VoucherLink/VoucherLink";

import styles from "./ChooseSubscription.module.scss";
import { StepsDots } from "../StepsDots";
import Button from "../../../../components/Button";
import { useCreatePaymentOrder } from "../../hooks/useCreatePaymentOrder";
import { useBuildLocalizedAbsolutedUrl } from "hooks";
import { PaymentError } from "../PaymentError";

export const ChooseSubscription = () => {
  const [selectedOffer, setSelectedOffer] = useState<IPaymentOfferModel | null>(
    null
  );
  const { isRegisteredViaCampaign } = useIsAuthenticated();
  const { t } = useTranslation("translations");

  const { offers } = useOffers();
  const hasOffers = !!offers?.length;

  const buildLocalizedAbsolutedUrl = useBuildLocalizedAbsolutedUrl();
  const {
    data: paymentOrder,
    mutateAsync: createPaymentOrder,
    error: createPaymentOrderError,
  } = useCreatePaymentOrder();

  const selectOffer = useCallback(
    (offer: IPaymentOfferModel) => {
      setSelectedOffer(offer);
      if (offer.Id && offer.Handle && !paymentOrder) {
        createPaymentOrder({
          OfferId: offer.Id.toString(),
          OfferHandle: offer.Handle,
          AcceptUrl: buildLocalizedAbsolutedUrl(
            ROUTES.ASSIGNING_SUBSCRIPTION_SCREEN
          ),
          CancelUrl: buildLocalizedAbsolutedUrl(ROUTES.PAYMENT_SUBSCRIPTION),
        });
      }
    },
    [setSelectedOffer, createPaymentOrder]
  );

  const renderOffer = useCallback(
    (offer: IPaymentOfferModel) => {
      return (
        <OfferCard
          key={offer.Id}
          offer={offer}
          active={offer.Id === selectedOffer?.Id}
          handleSelect={selectOffer}
        />
      );
    },
    [selectedOffer]
  );

  useEffect(() => {
    const defaultOffer = offers?.find((offer) => offer.Default);
    if (defaultOffer) {
      selectOffer(defaultOffer);
    }
  }, [offers]);

  return (
    <div className={styles.container}>
      <StepsDots activeStep={4} />

      <h1 className={styles.title}>{t("web_payment_header")}</h1>

      <div className={styles.offers}>{offers?.map(renderOffer)}</div>

      <span className={styles.description}>
        {!!offers?.length
          ? t("web_payment_choose_subscription_description")
          : t("web_payment_choose_subscription_error")}
      </span>

      {createPaymentOrderError && (
        <div className="mt-md mb-md">
          <PaymentError
            error={createPaymentOrderError.Message?.toString() || ""}
          />
        </div>
      )}

      <Button
        href={paymentOrder?.CheckoutUrl}
        size="medium"
        variant="primary"
        className={styles.button}
      >
        {t("web_payment_select_subscription_button")}
      </Button>

      <Divider />

      {hasOffers && !isRegisteredViaCampaign && <VoucherLink />}
    </div>
  );
};

export default ChooseSubscription;
